import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Lock } from "lucide-react";

const SPARKLE_OVERLAY = "https://static.prod-images.emergentagent.com/jobs/2c122ba0-c4f3-4497-9a5a-0002cdd6e57f/images/4c1845e26a2fcf5b3f9e3f9cca4d1fa8fc996f549630b0ad333e561292a49bc5.png";

export default function AdminLoginPage() {
  const { login, verify2FA } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.requires_2fa) {
        setChallenge(result.challenge);
        setLoading(false);
        return;
      }
      if (result.role !== "admin") {
        toast.error("Access denied", { description: "This portal is for staff only." });
        setLoading(false);
        return;
      }
      toast.success(`Welcome, ${result.name}`);
      nav("/admin");
    } catch (e) {
      toast.error("Invalid credentials", { description: e?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verify2FA(challenge, code);
      if (u.role !== "admin") {
        toast.error("Access denied");
        setLoading(false);
        return;
      }
      toast.success(`Welcome, ${u.name}`);
      nav("/admin");
    } catch (e) { toast.error("Invalid 2FA code", { description: e?.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] relative bg-[#0B1612] overflow-hidden flex items-center justify-center px-4 py-16" data-testid="admin-login-page">
      <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#063A24]/90 via-[#0B1612]/95 to-[#000]/95" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.32em] mb-6">
            <ShieldCheck className="w-3 h-3" />
            Restricted Area
          </div>
          <img
            src="https://customer-assets.emergentagent.com/job_premium-clean-atl/artifacts/x1iqqas7_ShoNics%20Logo.jpg"
            alt="ShoNics"
            className="h-20 w-auto mx-auto bg-white p-2 rounded-2xl mb-6"
          />
          <h1 className="font-serif text-4xl text-white">Staff Portal</h1>
          <p className="text-white/60 text-sm mt-2">{challenge ? "Two-Factor verification" : "Authorized personnel only."}</p>
        </div>

        {!challenge ? (
          <form onSubmit={submit} className="bg-white/5 backdrop-blur-2xl border border-[#D4AF37]/20 rounded-2xl p-8 shadow-[0_30px_80px_rgba(0,0,0,0.5)]" data-testid="admin-login-form">
            <label className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#D4AF37]">Staff email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="admin@shonicskleaningsolutions.com"
              className="w-full px-5 py-3.5 mt-2 mb-5 rounded-full bg-white/10 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none transition-colors"
              data-testid="admin-login-email" />
            <label className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#D4AF37]">Password</label>
            <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              className="w-full px-5 py-3.5 mt-2 mb-4 rounded-full bg-white/10 border border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none transition-colors"
              data-testid="admin-login-password" />
            <Link to="/forgot-password" className="text-xs text-white/60 hover:text-[#D4AF37] block mb-6">Forgot password?</Link>
            <button disabled={loading} className="w-full inline-flex items-center justify-center bg-[#D4AF37] text-[#0B1612] px-7 py-3.5 rounded-full font-semibold tracking-wide hover:bg-white transition-colors disabled:opacity-60" data-testid="admin-login-submit">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : <><Lock className="w-4 h-4 mr-2" /> Sign in to Console</>}
            </button>
            <div className="mt-6 text-center">
              <Link to="/login" className="text-xs text-white/50 hover:text-white">Customer login →</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={verify} className="bg-white/5 backdrop-blur-2xl border border-[#D4AF37]/20 rounded-2xl p-8 shadow-[0_30px_80px_rgba(0,0,0,0.5)]" data-testid="admin-2fa-form">
            <p className="text-sm text-white/70 mb-5 text-center">Enter the 6-digit code from your authenticator app.</p>
            <input value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
              placeholder="000000" inputMode="numeric" maxLength={6} autoFocus
              className="w-full px-4 py-4 mb-6 rounded-xl bg-white/10 border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none text-3xl tracking-[0.4em] text-center font-mono"
              data-testid="admin-2fa-code" />
            <button disabled={loading || code.length !== 6} className="w-full inline-flex items-center justify-center bg-[#D4AF37] text-[#0B1612] px-7 py-3.5 rounded-full font-semibold tracking-wide hover:bg-white transition-colors disabled:opacity-60" data-testid="admin-2fa-submit">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : "Verify and continue"}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setCode(""); }} className="block w-full text-center mt-5 text-xs text-white/50 hover:text-white">Use a different account</button>
          </form>
        )}

        <div className="text-center mt-8 text-[10px] uppercase tracking-[0.28em] text-white/30">
          ShoNics Kleaning Solutions · Operations
        </div>
      </div>
    </div>
  );
}
